import React, { useCallback } from 'react';
import {
  ContentType,
  formatMinutes,
  InformationCard,
  InformationCardState,
  RelatedResourcesPanel,
  useDeviceSize,
} from '@atlassian/learning-components';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import { FIELD_ID, getDuration } from 'src/pages/CoursePage/utils';
import { useSaveContent } from 'src/hooks/useSaveContent';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { CourseLoaderProps } from 'src/pages/CoursePage/loader';
import { useLessonProgress } from 'src/store/progress/progressStore';
import { LearningProgressStatus } from 'src/services/http/progress';
import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';
import { useStartContent } from 'src/hooks/useStartContent';
import { getSlug } from 'src/utils/slug';
import { getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { shortenDurationString } from 'src/pages/LearningPathPage/utils';

interface InformationCardWrapperProps {
  urlToShare: string;
}

export const CoursePageInformationCard: React.FC<InformationCardWrapperProps> = ({ urlToShare }) => {
  const { course, learningPathsByCourse } = useLoaderData() as CourseLoaderProps;
  const { user, isLoading } = useAtlassianUser();
  const { isContentSaved, saveContent } = useSaveContent({
    contentId: course?.sys.id,
    contentType: ContentType.COURSE,
    user,
  });
  const deviceWidth = useDeviceSize();
  const location = useLocation();
  const navigate = useNavigate();
  const [{ progressForLessons, isLoadingProgressForLessons }] = useLessonProgress();

  const lessons = course.lessonsCollection.items;
  const courseSlug = getSlug(course);
  const learningObjectives = course?.learningObjectives?.json
    ? documentToReactComponents(course.learningObjectives.json, getOptions(course.learningObjectives, deviceWidth))
    : '';
  const imageUrl = course.reward?.orangeLogicAsset?.imageUrl ?? course.reward?.badgeCdnUrl;

  const courseDuration = shortenDurationString(formatMinutes(getDuration(course)));

  const navigateToLesson = useCallback(
    (lessonSlug: string): void => {
      navigate(withPrefix(getLessonUrl({ lessonSlug }), location.pathname.replace(/\/$/, '')));
    },
    [navigate, location],
  );

  const navigateToLearningPath = useCallback(
    (pathSlug: string): void => {
      navigate(`/path/${pathSlug}`);
    },
    [navigate],
  );

  const startCourse = useStartContent({
    items: course?.lessonsCollection?.items,
    progress: progressForLessons,
    contentSlug: courseSlug,
    contentType: ContentType.COURSE,
    userId: user?.account_id,
    navigateToItem: navigateToLesson,
    fieldId: FIELD_ID,
  });

  const calculateProgress = (): number => {
    if (!progressForLessons || isLoadingProgressForLessons || !lessons) {
      return 0;
    }

    if (progressForLessons?.overallStatus === LearningProgressStatus.COMPLETED) {
      return 100;
    }

    const numberOfCompletedLessons = progressForLessons.lessons.filter(
      (l) =>
        course.lessonsCollection.items.find((lesson) => lesson.sys.id === l.lessonId) &&
        l.status === LearningProgressStatus.COMPLETED,
    ).length;

    return Math.round((numberOfCompletedLessons / course.lessonsCollection.items.length) * 100);
  };

  const getCourseState = (): InformationCardState => {
    if (isLoading || isLoadingProgressForLessons) {
      return InformationCardState.LOADING;
    }

    if (!progressForLessons || !user) {
      return InformationCardState.DEFAULT;
    }

    if (progressForLessons?.overallStatus === LearningProgressStatus.IN_PROGRESS) {
      return InformationCardState.IN_PROGRESS;
    }

    if (progressForLessons?.overallStatus === LearningProgressStatus.COMPLETED) {
      return InformationCardState.FINISHED;
    }

    return InformationCardState.DEFAULT;
  };

  return (
    <div css={infoPanelStyles}>
      <InformationCard
        data-testid="information-card"
        badge={imageUrl}
        contentType={ContentType.COURSE}
        progress={calculateProgress()}
        duration={courseDuration}
        description={learningObjectives}
        cardState={getCourseState()}
        isSaved={isContentSaved}
        onSave={user && saveContent}
        urlToShare={urlToShare}
        onClick={startCourse}
        title={course?.learningObjectivesTitle}
      />
      {learningPathsByCourse && learningPathsByCourse.length > 0 && (
        <RelatedResourcesPanel
          data-testid="related-resources-panel"
          title="This course appears in these learning paths"
          resources={learningPathsByCourse}
          onResourceClick={(slug) => navigateToLearningPath(slug)}
        />
      )}
    </div>
  );
};

const infoPanelStyles = css({
  gap: token('space.200'),
  display: 'flex',
  flexDirection: 'column',
});
